import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IAlertsSubscriber } from "../../faults/DTOs";
import { LoadingState } from "../LoadingState";
import { deleteAlertsSubscriber, getAlertsSubscribers, upsertAlertsSubscriber } from "../../faults/API";
import { beginRequest, completeRequest, rejectRequest, RequestState } from "./RequestState";

interface IFaultStoreState extends RequestState {
  loadingState: LoadingState;
  subscribers: IAlertsSubscriber[];
}

export const initState: IFaultStoreState = {
  loadingState: LoadingState.None,
  subscribers: [],
};

export const alertsSubscribersSlice = createSlice({
  name: "alertsSubscribers",
  initialState: initState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchAlertsSubscribers.pending, (state, action) => {
        beginRequest(state, action.meta.requestId);
      })
      .addCase(fetchAlertsSubscribers.fulfilled, (state, action) => {
        if (!completeRequest(state, action.meta.requestId)) {
          return;
        }
        const { subscribers } = action.payload;
        state.loadingState = LoadingState.Complete;
        state.subscribers = subscribers;
      })
      .addCase(fetchAlertsSubscribers.rejected, (state, action) => {
        rejectRequest(state, action);
      })
      .addCase(removeAlertsSubscriber.pending, (state) => {
        state.loadingState = LoadingState.Pending;
      })
      .addCase(removeAlertsSubscriber.fulfilled, (state) => {
        state.loadingState = LoadingState.Complete;
      })
      .addCase(removeAlertsSubscriber.rejected, (state) => {
        state.loadingState = LoadingState.Error;
      })
      .addCase(updateAlertsSubscriber.pending, (state) => {
        state.loadingState = LoadingState.Pending;
      })
      .addCase(updateAlertsSubscriber.fulfilled, (state) => {
        state.loadingState = LoadingState.Complete;
      })
      .addCase(updateAlertsSubscriber.rejected, (state) => {
        state.loadingState = LoadingState.Error;
      }),
});

export const fetchAlertsSubscribers = createAsyncThunk(
  "faults/fetchAlertsSubscribers",
  async (_, { signal }) => {
    const subscribers = await getAlertsSubscribers(signal);
    return {
      subscribers: subscribers,
    };
  }
);

export const removeAlertsSubscriber = createAsyncThunk(
  "faults/removeAlertsSubscriber",
  async (userId: string, { dispatch, signal }) => {
    await deleteAlertsSubscriber(userId, signal);
    await dispatch(fetchAlertsSubscribers()).unwrap();
    return {
      userId,
    };
  }
);

export const updateAlertsSubscriber = createAsyncThunk(
  "faults/upsertAlertsSubscriber",
  async (subscriber: IAlertsSubscriber, { dispatch, signal }) => {
    await upsertAlertsSubscriber(subscriber, signal);
    dispatch(fetchAlertsSubscribers());
  }
);

export const alertsSubscribersReducer = alertsSubscribersSlice.reducer;

// The service has no atomic bulk endpoint. Save each site separately so failures
// identify an exact retry scope, then reload even if one or more writes failed.
export const replaceSiteSubscriptions = createAsyncThunk(
  "faults/replaceSiteSubscriptions",
  async ({ userId, siteIds, alertIds, rules }: IAlertsSubscriber, { dispatch, signal }) => {
    const failed: string[] = [];
    for (const siteId of siteIds) {
      try {
        await upsertAlertsSubscriber(
          { userId, siteIds: [siteId], alertIds, ...(rules != null ? { rules } : {}) },
          signal
        );
      } catch {
        failed.push(siteId);
      }
    }
    await dispatch(fetchAlertsSubscribers()).unwrap();
    return failed;
  }
);
